/* Verbindung zwischen Editor und Backend-Oberfläche: erzeugt Sitzung und Karten-Verbindung, öffnet die React-Dialoge
 * und hält die wenigen DOM-Elemente aktuell (Server-Knopf, Statuszeile). Alles, was den Editor selbst betrifft, kommt über AppApi. */
import I18n from '../i18n.ts';
import Keys from '../keys.ts';
import Theme from '../theme.js';
import Backend from '../net/backend.ts';
import { BackendSession } from '../net/session.ts';
import type { SettingsPort } from '../net/session.ts';
import { CloudLink } from '../net/cloud.ts';
import { CompileLink, isActive } from '../net/compile.ts';
import { QueueLink } from '../net/queue.ts';
import { QueueDialog } from './QueueDialog.tsx';
import type { CheckItem } from '../model/types.ts';
import { planMapWads, planWadSync, sha256Hex } from '../net/mapwads.ts';
import { planFgdSync, planMapFgds } from '../net/mapfgd.ts';
import { isAutoUpload, planDroppedUploads } from '../net/assetsync.ts';
import { FGD_DIR, fgdNames } from '../model/modprofile.ts';
import { planMapSounds, soundKey } from '../net/sounds.ts';
import { DEFAULT_PACING, runUploads } from '../net/uploadqueue.ts';
import { WarningsDialog } from './dialogs/SmallDialogs.tsx';
import { closeAllDialogs, openDialog } from './modal.tsx';
import { SettingsDialog } from './SettingsDialog.tsx';
import type { SettingsHost, SettingsTabId } from './SettingsDialog.tsx';
import { LoginDialog } from './LoginDialog.tsx';
import { CloudDialog } from './CloudDialog.tsx';
import { SaveDialog } from './SaveDialog.tsx';
import { CompileDialog } from './CompileDialog.tsx';
import { ConflictDialog } from './ConflictDialog.tsx';
import type { BackendActions } from './BackendTab.tsx';

/** Was die Oberfläche vom Editor braucht */
export interface AppApi {
  status(msg: string): void;
  exportText(): string;
  isBsp(): boolean;
  /** Hinweise zum Compile-Aufwand der aktuellen Karte */
  prechecks(): CheckItem[];
  loadText(text: string, name: string, label: string): void;
  openFile(file: File): Promise<void>;
  /** Sound unter dem Schlüssel (Pfad ab sound/) in den Editor laden */
  addSound(file: File, key?: string): Promise<void>;
  /** Schlüssel der Sounds, die die Karte braucht, und der schon geladenen */
  soundNames(): { wanted: string[]; loaded: string[] };
  /** Dateiauswahl für eine Karte von der Festplatte öffnen */
  openLocal(): void;
  /** Vordefinierte Karten (Auswahl im Öffnen-Dialog) */
  samples(): { id: string; label: string }[];
  openSample(id: string): void;
  /** Namen der geladenen WADs im Format „datei.wad (Anzahl)“ */
  wadNames(): string[];
  /** wad-Eintrag der Welt-Entity der aktuellen Karte */
  worldWad(): string;
  /** xf_fgd-Eintrag der Welt-Entity und die schon geladenen FGD-Namen; Text einer FGD einsetzen */
  worldFgd(): string;
  fgdLoaded(): string[];
  addFgdText(name: string, text: string): void;
  getFileName(): string;
  setFileName(name: string): void;
  setSettingsTab(tab: SettingsTabId): void;
}

const baseName = (p: string): string => p.replace(/^.*\//, '');

/** Lokale Einstellungen lesen und anwenden (Sprache, Design, Tastenkürzel); die Prüfung macht UserSettings.sanitize */
const settingsPort: SettingsPort = {
  allow: () => ({ langs: Object.keys(I18n.LANGS), themes: Object.keys(Theme.THEMES), keyIds: Keys.list.map((k) => k.id) }),
  read: () => ({ lang: I18n.lang, theme: Theme.snapshot(), keys: Keys.exportCustom() }),
  apply: (p) => {
    if (p.lang && p.lang !== I18n.lang) I18n.setLang(p.lang);
    if (p.theme) Theme.replace(p.theme);
    if (p.keys) Keys.importCustom(p.keys);
  }
};

export function initBackendUi(api: AppApi) {
  const t = I18n.t;
  const session = new BackendSession({
    storage: localStorage, settings: settingsPort,
    notify: (n) => api.status(n.type === 'session-expired' ? t('be.session_expired') : t('be.settings_failed', { msg: n.msg }))
  });
  const cloud = new CloudLink({
    client: () => session.client, getText: () => api.exportText(), isBsp: () => api.isBsp(),
    loadText: (text, name, label) => api.loadText(text, name, label), t: I18n.t, say: (m) => api.status(m)
  });

  const compile = new CompileLink({ client: () => session.client, t: I18n.t, say: (m) => api.status(m), finished: () => chrome() });

  const queue = new QueueLink({ client: () => session.client, t: I18n.t, isAdmin: () => session.getSnapshot().user?.role === 'admin' });

  // ---- Anmeldung
  const login = async (): Promise<boolean> => !!(await openDialog<boolean>((close) => <LoginDialog session={session} onDone={(ok) => close(ok)} />));
  const ensureAuth = async (): Promise<boolean> => session.getSnapshot().authed || login();

  // ---- Aktionen der Backend-Seite (Dateien in den Editor laden, hochladen, herunterladen)
  const actions: BackendActions = {
    say: (m) => api.status(m),
    login,
    async load(path) {
      const name = baseName(path);
      try {
        if (/\.wad$/i.test(name) && api.wadNames().some((n) => n.toLowerCase().indexOf(name.toLowerCase() + ' (') === 0)) { api.status(t('be.already_loaded', { path })); return false; }
        const file = new File([await (session.client as NonNullable<typeof session.client>).fetchBuffer(path)], name);
        if (/^sounds\//.test(path)) await api.addSound(file, path.slice(7)); else await api.openFile(file);
        return true;
      } catch (err) { api.status(t('be.load_failed', { path, msg: (err as Error).message })); return false; }
    },
    async download(path) {
      try {
        const url = URL.createObjectURL(new Blob([await (session.client as NonNullable<typeof session.client>).fetchBuffer(path)]));
        const a = document.createElement('a'); a.href = url; a.download = baseName(path); document.body.append(a); a.click(); a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 10000);
      } catch (err) { api.status(t('be.load_failed', { path, msg: (err as Error).message })); }
    },
    // Stapel mit Tempo (Rate-Limit des Backends); Admins sind davon ausgenommen und laden ohne Pausen
    async upload(files, onProgress) {
      const c = session.client as NonNullable<typeof session.client>;
      const unlimited = session.getSnapshot().user?.role === 'admin';
      const items = files.map((it) => {
        const f = 'file' in it ? it.file : it, rel = 'file' in it ? it.rel : (f as File & { webkitRelativePath?: string }).webkitRelativePath;
        return { file: f, path: Backend.targetPath(f.name, rel) };
      });
      const r = await runUploads(items, {
        upload: (it) => c.upload(it.path, it.file), nameOf: (it) => it.file.name, pacing: unlimited ? null : DEFAULT_PACING,
        onProgress: (p) => { onProgress?.(p); if (p.waitMs <= 0) api.status(t('be.upload_progress', { done: Math.min(p.done + 1, p.total), total: p.total, name: p.name })); }
      });
      if (r.failed) api.status(t('be.upload_failed', { name: r.failed.item.file.name, msg: r.failed.error.message }));
      else api.status(t('be.uploaded', { count: r.done }));
      return r.done;
    },
    // Sounds der Karte (ambient_generic) vom Server holen; Pfade stehen im Feld message, Dateien unter sounds/<Pfad>
    async loadMapSounds(auto?: boolean) {
      const c = session.client;
      if (!c || session.getSnapshot().state !== 'ok' || !session.getSnapshot().authed) return 0;
      const { wanted, loaded } = api.soundNames();
      if (!wanted.length) return 0;
      let items; try { items = await c.list('sounds/'); } catch (err) { if (!auto) api.status(t('be.list_failed', { msg: (err as Error).message })); return 0; }
      const plan = planMapSounds(wanted, items, loaded);
      let n = 0;
      for (const l of plan.load) {
        try { await api.addSound(new File([await c.fetchBuffer(l.path)], baseName(l.path)), l.key); n++; }
        catch (err) { api.status(t('be.load_failed', { path: l.path, msg: (err as Error).message })); }
      }
      if (n || (!auto && plan.missing.length)) api.status(t(plan.missing.length ? 'be.sounds_result_missing' : 'be.sounds_result', { ok: n + plan.have.length, total: n + plan.have.length + plan.missing.length, missing: plan.missing.join(', ') }));
      return n;
    },
    // FGD-Dateien der Karte (Mod-Profil) vom Server holen; Namen stehen in xf_fgd, Dateien unter fgd/<Name>
    async loadMapFgds(auto?: boolean) {
      const c = session.client;
      if (!c || session.getSnapshot().state !== 'ok' || !session.getSnapshot().authed) return 0;
      const names = fgdNames(api.worldFgd());
      if (!names.length) return 0;
      let items; try { items = await c.list(FGD_DIR); } catch (err) { if (!auto) api.status(t('be.list_failed', { msg: (err as Error).message })); return 0; }
      const plan = planMapFgds(names, items, api.fgdLoaded());
      let n = 0;
      for (const l of plan.load) {
        try { api.addFgdText(l.name, new TextDecoder().decode(await c.fetchBuffer(l.path))); n++; }
        catch (err) { api.status(t('be.load_failed', { path: l.path, msg: (err as Error).message })); }
      }
      if (plan.missing.length) api.status(t('be.fgd_missing', { names: plan.missing.join(', ') }));
      return n;
    },
    async loadMapWads(auto?: boolean) {
      const c = session.client;
      if (!c || session.getSnapshot().state !== 'ok' || !session.getSnapshot().authed) return 0;
      if (!Backend.wadNames(api.worldWad()).length) { if (!auto) api.status(t('be.map_wads_none')); return 0; }
      let items; try { items = await c.list('', 'wad'); } catch (err) { api.status(t('be.list_failed', { msg: (err as Error).message })); return 0; }
      const plan = planMapWads(api.worldWad(), items, api.wadNames(), hashes);
      let n = 0;
      for (const path of plan.load) if (await actions.load(path)) n++;
      if (plan.missing.length || plan.differ.length) {
        if (plan.missing.length) api.status(t('be.map_wads_result', { ok: n + plan.have.length, total: plan.wanted.length, missing: plan.missing.join(', ') }));
        else api.status(t('be.wad_differs', { path: plan.differ.join(', ') }));
        void openDialog((close) => <WarningsDialog warnings={[...plan.missing.map((m) => t('be.wad_missing', { name: m })), ...plan.differ.map((m) => t('be.wad_differs', { path: m }))]} onClose={() => close()} />);
      } else if (!auto || n) api.status(t('be.map_wads_ok', { ok: n + plan.have.length, total: plan.wanted.length }));
      return n;
    }
  };

  // Lokal geladene WAD: ist man angemeldet, wird der Name auf dem Server gesucht. Fehlt er, wird hochgeladen; gibt es ihn mit
  // anderem Inhalt (SHA-256), fragt der Editor, ob die Server-Version ersetzt werden soll.
  const uploading = new Set<string>();
  const hashes: Record<string, string> = {};   // Hash der lokal geladenen WADs (Name klein geschrieben), für den Abgleich beim Öffnen einer Karte
  async function syncWad(file: File): Promise<void> {
    const c = session.client, s = session.getSnapshot();
    const key = file.name.toLowerCase();
    if (!c || s.state !== 'ok' || !s.authed || !s.info?.capabilities?.assets || uploading.has(key)) return;
    uploading.add(key);
    try {
      const hash = await sha256Hex(await file.arrayBuffer());
      if (hash) hashes[key] = hash;
      const plan = planWadSync(file.name, hash, await c.list('', 'wad'));
      if (plan.kind === 'upload') { await c.upload(plan.path, file); api.status(t('be.wad_uploaded', { path: plan.path })); }
      else if (plan.kind === 'differs') {
        if (window.confirm(t('be.wad_differs_ask', { path: plan.path }))) { await c.upload(plan.path, file); api.status(t('be.wad_replaced', { path: plan.path })); }
        else api.status(t('be.wad_differs', { path: plan.path }));
      }
    } catch (err) { api.status(t('be.upload_failed', { name: file.name, msg: (err as Error).message })); }
    finally { uploading.delete(key); }
  }

  // Lokal geladene FGD: fehlt der Name auf dem Server, wird sie hochgeladen; bei anderem Inhalt fragt der Editor wie bei WADs
  async function syncFgd(file: File): Promise<void> {
    const c = session.client, s = session.getSnapshot();
    if (!c || s.state !== 'ok' || !s.authed || !s.info?.capabilities?.assets) return;
    try {
      const hash = await sha256Hex(await file.arrayBuffer());
      const plan = planFgdSync(file.name, hash, await c.list(FGD_DIR));
      if (plan.kind === 'upload') { await c.upload(plan.path, file); api.status(t('be.fgd_uploaded', { path: plan.path })); }
      else if (plan.kind === 'differs') {
        if (window.confirm(t('be.fgd_differs_ask', { path: plan.path }))) { await c.upload(plan.path, file); api.status(t('be.fgd_replaced', { path: plan.path })); }
        else api.status(t('be.fgd_differs', { path: plan.path }));
      }
    } catch (err) { api.status(t('be.upload_failed', { name: file.name, msg: (err as Error).message })); }
  }

  // Beim Ziehen in den Editor: alle bekannten Dateien (Sprites, Modelle, Sounds, Skybox-Seiten) auf den Server laden, wenn man angemeldet ist.
  // Fehlendes wird hochgeladen, Gleiches übersprungen; bei anderem Inhalt unter demselben Pfad fragt der Editor einmal für alle.
  async function uploadDropped(items: { file: File; rel?: string }[]): Promise<void> {
    const c = session.client, s = session.getSnapshot();
    if (!c || s.state !== 'ok' || !s.authed || !s.info?.capabilities?.assets) return;
    const known = items.filter((i) => isAutoUpload(i.file.name));
    if (!known.length) return;
    try {
      const list = await c.list();
      const hashed = await Promise.all(known.map(async (i) => ({ item: i, name: i.file.name, rel: i.rel, hash: await sha256Hex(await i.file.arrayBuffer()) })));
      const plan = planDroppedUploads(hashed, list);   // gleiche Reihenfolge wie hashed (alle sind bekannte Arten)
      const itemsOf = (kind: 'upload' | 'differs') => hashed.filter((_h, i) => plan[i].kind === kind).map((h) => h.item);
      const differ = plan.filter((p) => p.kind === 'differs');
      let send = itemsOf('upload');
      if (differ.length && window.confirm(t('be.dnd_differs_ask', { count: differ.length, names: differ.slice(0, 5).map((d) => d.path).join(', ') }))) send = send.concat(itemsOf('differs'));
      if (!send.length) { if (plan.length) api.status(t('be.dnd_same', { count: plan.length })); return; }
      await actions.upload(send.map((i) => (i.rel ? { file: i.file, rel: i.rel } : i.file)));
    } catch (err) { api.status(t('be.upload_failed', { name: known[0].file.name, msg: (err as Error).message })); }
  }

  // ---- Dialoge
  const settingsHost: SettingsHost = { rememberTab: (tab) => api.setSettingsTab(tab) };
  function openSettings(tab?: SettingsTabId): void {
    closeAllDialogs();
    void openDialog((close) => <SettingsDialog host={settingsHost} session={session} actions={actions} initialTab={tab} onClose={() => close()} />);
  }
  // Öffnen-Dialog: auch ohne Anmeldung (Datei, Beispiele); die Serverliste erscheint nach der Anmeldung
  async function openCloud(): Promise<void> {
    closeAllDialogs();
    void openDialog((close) => (
      <CloudDialog session={session} cloud={cloud} onClose={() => close()}
        host={{ setFileName: (n) => api.setFileName(n), openLocal: () => api.openLocal(), samples: () => api.samples(), openSample: (id) => api.openSample(id), login }} />
    ));
  }
  async function openSave(): Promise<void> {
    if (!(await ensureAuth())) return;
    closeAllDialogs();
    void openDialog((close) => (
      <SaveDialog cloud={cloud} onClose={() => close()}
        host={{ fileName: () => api.getFileName(), setFileName: (n) => api.setFileName(n), isBsp: () => api.isBsp() }} />
    ));
  }
  async function openCompile(): Promise<void> {
    if (!(await ensureAuth())) return;
    closeAllDialogs();
    const admin = session.getSnapshot().user?.role === 'admin';
    void openDialog((close) => (
      <CompileDialog compile={compile} cloud={cloud} onClose={() => close()}
        host={{ admin, isBsp: () => api.isBsp(), openFile: (f) => api.openFile(f), openCloud: () => { void openCloud(); }, openQueue: () => { void openQueue(); }, prechecks: () => api.prechecks() }} />
    ));
  }
  async function openQueue(): Promise<void> {
    if (!(await ensureAuth())) return;
    closeAllDialogs();
    const u = session.getSnapshot().user;
    void openDialog((close) => (
      <QueueDialog queue={queue} onClose={() => close()}
        host={{ admin: u?.role === 'admin', userId: u?.id ?? '', openJob: (j) => { compile.attach(j); void openCompile(); } }} />
    ));
  }
  // Konflikt beim Speichern: Dialog zeigen (einmal); schließt der Benutzer ihn mit Esc, bleibt das Speichern pausiert
  let conflictOpen = false;
  cloud.subscribe(() => {
    if (!cloud.getSnapshot().conflict || conflictOpen) return;
    conflictOpen = true;
    void openDialog((close) => <ConflictDialog cloud={cloud} onDone={() => close()} />).finally(() => { conflictOpen = false; if (cloud.getSnapshot().conflict) cloud.dismissConflict(); });
  });

  // ---- DOM-Elemente: Server-Knopf und Statuszeile
  function chrome(): void {
    const s = session.getSnapshot(), c = cloud.getSnapshot();
    const btn = document.getElementById('b-save'), el = document.getElementById('map-badge');
    if (btn) btn.hidden = s.state !== 'ok';   // „Speichern“ (Server)
    const cb = document.getElementById('b-compile');
    if (cb) {
      cb.hidden = !(s.state === 'ok' && s.info && s.info.capabilities && s.info.capabilities.compile);
      cb.classList.toggle('busy', isActive(compile.getSnapshot().job));
    }
    if (el) {
      // Woher die geöffnete Karte stammt: Server (verbunden, ggf. pausiert/abgemeldet/nur lesbar) oder lokal (Datei, Beispiel, neu)
      el.dataset.kind = c.link ? (!c.authed || c.paused ? 'warn' : 'server') : 'local';
      el.textContent = !c.link ? t('cloud.local', { name: api.getFileName() }) : !c.authed ? t('cloud.offline', { name: c.link.name }) : c.paused ? t('cloud.paused', { name: c.link.name }) : c.link.access === 'read' ? t('cloud.linked_ro', { name: c.link.name, rev: c.link.rev }) : t('cloud.linked', { name: c.link.name, rev: c.link.rev });
    }
  }
  session.subscribe(chrome); cloud.subscribe(chrome); compile.subscribe(chrome); I18n.onChange(chrome);
  // Nach Anmeldung (oder Neuladen mit gemerkter Anmeldung): einen laufenden Job wieder beobachten
  let resumed = false;
  session.subscribe(() => {
    const s = session.getSnapshot();
    if (s.state === 'ok' && s.authed && s.info && s.info.capabilities && s.info.capabilities.compile) { if (!resumed) { resumed = true; void compile.resume(); } } else { resumed = false; if (!s.authed) compile.detach(); }
  });
  // Die WADs der geöffneten Karte holen, sobald (wieder) angemeldet ist, z. B. nach dem Neuladen mit gemerkter Anmeldung
  let wadsFor = false;
  session.subscribe(() => {
    const s = session.getSnapshot(), ok = s.state === 'ok' && s.authed;
    if (ok && !wadsFor) { void actions.loadMapWads(true); void actions.loadMapSounds(true); void actions.loadMapFgds(true); }
    wadsFor = ok;
  });
  // Anmeldung oder Abmeldung ändert, ob gespeichert wird
  session.subscribe(() => cloud.refresh());

  return { session, cloud, compile, autoLoadWads: () => { void actions.loadMapWads(true); void actions.loadMapSounds(true); void actions.loadMapFgds(true); }, syncWad: (f: File) => { void syncWad(f); }, syncFgd: (f: File) => { void syncFgd(f); }, uploadDropped: (items: { file: File; rel?: string }[]) => { void uploadDropped(items); }, openSettings, openCloud, openSave, openCompile, openQueue, ensureAuth, chrome };
}
export type BackendUi = ReturnType<typeof initBackendUi>;
